import type { ProjectMember } from "./types.ts";

export type MemberRateGroup = {
  employeeId: number;
  primary: ProjectMember;
  additional: ProjectMember[];
};

const categoryOrder: Record<NonNullable<ProjectMember["memberCategory"]>, number> = {
  ACTIVE: 0,
  SHADOW: 1,
  DEALLOCATED: 2,
};

function rateOrder(member: ProjectMember): number {
  const category = categoryOrder[member.memberCategory ?? "DEALLOCATED"] ?? 3;
  // A zero rate is configured; only null means the project has no commercial rate.
  return category * 2 + (member.commercialRate == null ? 1 : 0);
}

/** Keep every rate record, but show the current membership first for each employee. */
export function groupMemberRates(members: ProjectMember[]): MemberRateGroup[] {
  const byEmployee = new Map<number, ProjectMember[]>();
  for (const member of members) {
    const records = byEmployee.get(member.employeeId) ?? [];
    records.push(member);
    byEmployee.set(member.employeeId, records);
  }

  return Array.from(byEmployee, ([employeeId, records]) => {
    const ordered = [...records].sort((left, right) => rateOrder(left) - rateOrder(right));
    return { employeeId, primary: ordered[0], additional: ordered.slice(1) };
  }).sort((left, right) =>
    rateOrder(left.primary) - rateOrder(right.primary) ||
    left.primary.employeeName.localeCompare(right.primary.employeeName) ||
    left.employeeId - right.employeeId,
  );
}
